/**
 * 音乐室场景：东方 Music Room 风格的 BGM 试听室。
 * ↑↓ 移动光标，Z 播放/停止（同一首再按 Z 停止），X 返回标题。
 * 进入时不自动播放（先停掉标题曲）；下方显示光标所选曲目的创作背景。
 * 曲目数据见 public/js/content/musicTracks.js。
 */
import { VIEW_W, VIEW_H } from '../config.js';
import { Input } from '../engine/input.js';
import { menuNav } from '../engine/menu.js';
import { MUSIC_TRACKS } from '../content/musicTracks.js';
import { t, L } from '../i18n.js';
import { drawFittedText } from '../engine/wrapText.js';

const LIST_Y = 96;
const ROW_H = 20;
const VISIBLE = 13; // 可视行数（96+13×20=356 < PANEL_Y）；超出滚动跟随光标
const PANEL_Y = 372;

/** 评论面板布局（tools/ui-fit-check.mjs 据此做溢出回归断言，改动需同步过检） */
export const COMMENT_PANEL = { x: 36, y: PANEL_Y + 44, maxWidth: VIEW_W - 72, bottomLimit: VIEW_H - 10 };

export const MusicRoomScene = {
  mgr: null,

  enter() {
    this.idx = 0;
    this.playingKey = null;
    this.loadingKey = null;
    // 试听室从安静开始：停掉标题曲，不自动播放
    this.mgr.shared.audio.stopBgm();
  },

  exit() {
    // 正在播放主菜单曲（bgm_title）则不中断：回标题后原进度续播
    // （title.enter 的 playBgm 同 key 早退，进度自然保留）
    if (this.playingKey !== 'bgm_title') {
      this.mgr.shared.audio.stopBgm();
    }
    this.playingKey = null;
  },

  update() {
    const audio = this.mgr.shared.audio;
    const n = MUSIC_TRACKS.length;
    if (Input.pressed('cancel')) {
      audio.playSfx('select');
      this.mgr.switchTo('title');
      return;
    }
    const nav = menuNav(Input, audio, this.idx, n);
    if (nav !== null) this.idx = nav;
    if (Input.pressed('confirm')) {
      const track = MUSIC_TRACKS[this.idx];
      if (this.playingKey === track.key) {
        audio.stopBgm(); // 同一首再按 Z：停止
        this.playingKey = null;
        this.loadingKey = null; // 显式停止后， pending 加载不得再自动开播
      } else if (this.mgr.shared.assets.audio(track.key)) {
        audio.playBgm(track.key);
        this.playingKey = track.key;
        this.loadingKey = null; // 播了已加载曲目：清 pending（否则它就绪后顶掉本曲——审计发现）
      } else {
        // BGM 懒加载：未加载曲目点播 → 行内 ⋯ 标记，就绪自动开播（不 block 光标）
        this.loadingKey = track.key;
        this.mgr.shared.assets.ensureAudio(track.key).then((ok) => {
          // ok=false = 加载失败：不得显示"播放中"（审计发现）
          if (ok && this.loadingKey === track.key && this.mgr.current === this && !this.playingKey) {
            audio.playBgm(track.key);
            this.playingKey = track.key;
          }
          if (this.loadingKey === track.key) this.loadingKey = null;
        });
      }
      audio.playSfx('select');
    }
  },

  draw(r) {
    const assets = this.mgr.shared.assets;
    const bg = assets.img('bg1');
    if (bg) {
      const sc = Math.max(VIEW_W / bg.width, VIEW_H / bg.height);
      r.sprite(bg, VIEW_W / 2, VIEW_H / 2, { scale: sc, alpha: 0.3 });
    }
    r.rect(0, 0, VIEW_W, VIEW_H, { fill: '#000', alpha: 0.55 });

    r.text(t('music.title'), VIEW_W / 2, 20, { size: 22, color: '#fff', align: 'center' });
    r.text(t('music.sub'), VIEW_W / 2, 50, { size: 11, color: '#8cf', align: 'center' });
    r.text(t('music.hint'), VIEW_W / 2, 72, { size: 11, color: '#889', align: 'center' });

    // 曲目列表（超过 VISIBLE 行时滚动跟随光标；曲目信息经 L() 按语言取；时长取自解码后的 AudioBuffer）
    const n = MUSIC_TRACKS.length;
    const maxStart = Math.max(0, n - VISIBLE);
    const start = Math.max(0, Math.min(this.idx - Math.floor(VISIBLE / 2), maxStart));
    if (start > 0) r.text('▲', VIEW_W / 2, LIST_Y - 10, { size: 10, color: '#889', align: 'center' });
    if (start + VISIBLE < n) r.text('▼', VIEW_W / 2, LIST_Y + VISIBLE * ROW_H, { size: 10, color: '#889', align: 'center' });
    for (let i = start; i < Math.min(n, start + VISIBLE); i++) {
      const tr = MUSIC_TRACKS[i];
      const y = LIST_Y + (i - start) * ROW_H;
      const sel = i === this.idx;
      const playing = this.playingKey === tr.key;
      const color = sel ? '#ff4' : '#ccd';
      if (sel) r.text('▶', 22, y, { size: 12, color: '#ff4' });
      const loading = this.loadingKey === tr.key;
      r.text(playing ? '♪' : loading ? '⋯' : String(i + 1).padStart(2, '0'), 44, y, { size: 12, color: playing ? '#6cf' : loading ? '#8cf' : color });
      r.text(L(tr.title), 76, y, { size: 13, color });
      const dur = assets.audio(tr.key)?.duration;
      if (dur) {
        const mm = Math.floor(dur / 60);
        const ss = String(Math.floor(dur % 60)).padStart(2, '0');
        r.text(`${mm}:${ss}`, 308, y + 1, { size: 10, color: sel ? '#9bd' : '#778', align: 'right' });
      }
      r.text(L(tr.subtitle), VIEW_W - 24, y + 1, { size: 10, color: sel ? '#ddb' : '#778', align: 'right' });
    }

    // 光标所选曲目的创作背景
    const tr = MUSIC_TRACKS[this.idx];
    r.rect(30, PANEL_Y - 4, VIEW_W - 60, 1, { fill: '#446' });
    const head = this.playingKey === tr.key ? `♪ ${L(tr.title)}` : L(tr.title);
    r.text(head, 36, PANEL_Y + 2, { size: 15, color: '#ff4' });
    r.text(L(tr.subtitle), 36, PANEL_Y + 24, { size: 11, color: '#8cf' });
    // 评论自适应：英文等长文语言可达 12 行，超出容量时逐级缩小字号保证不出屏
    drawFittedText(r, L(tr.comment), COMMENT_PANEL.x, COMMENT_PANEL.y, COMMENT_PANEL.maxWidth, COMMENT_PANEL.bottomLimit);
  },
};
